"use client";
import React, { useState } from "react";

export default function MeasurementChart() {
  const [isCm, setIsCm] = useState(false);
  const unit = isCm ? "cm" : "inches";

  const data = {
    upperWear: [
      { size: "XS", shoulder: "13.25-13.75", bust: "32-33", waist: "26-26.5", hip: "34-35" },
      { size: "S", shoulder: "14-14.5", bust: "34-35", waist: "28-28.5", hip: "36-37" },
      { size: "M", shoulder: "14.75-15.25", bust: "36-37", waist: "30-30.5", hip: "38-39" },
      { size: "L", shoulder: "15.5-16", bust: "38-39", waist: "32-32.5", hip: "40-41" },
      { size: "XL", shoulder: "16.25-16.75", bust: "40-41", waist: "34-34.5", hip: "42-43" },
    ],
    bottomWear: [
      { size: "26", waist: "26-27", hip: "34-35", thigh: "22-23" },
      { size: "28", waist: "28-29", hip: "36-37", thigh: "23-24" },
      { size: "30", waist: "30-31", hip: "38-39", thigh: "24-25" },
      { size: "32", waist: "32-33", hip: "40-41", thigh: "25-26" },
      { size: "34", waist: "34-35", hip: "42-43", thigh: "26-27" },
    ],
    howToMeasure: [
      {
        title: "Waist",
        description: "Measure around the narrowest part of the abdomen.",
        img: "https://oextech.blr1.digitaloceanspaces.com/OexTech/CrunchyFashionclothing%40aicodepro.com/CrunchyFashion/CrunchyFashion/waist.jpg",
      },
      {
        title: "Hip",
        description: "Measure around your hips at the widest point.",
        img: "https://oextech.blr1.digitaloceanspaces.com/OexTech/CrunchyFashionclothing%40aicodepro.com/CrunchyFashion/CrunchyFashion/hip.jpg",
      },
      {
        title: "Thigh",
        description: "Measure around the fullest part of your thigh.",
        img: "https://oextech.blr1.digitaloceanspaces.com/OexTech/CrunchyFashionclothing%40aicodepro.com/CrunchyFashion/CrunchyFashion/thigh.jpg",
      },
      {
        title: "Bust",
        description: "Measure around the fullest part of your bust.",
        img: "https://oextech.blr1.digitaloceanspaces.com/OexTech/CrunchyFashionclothing%40aicodepro.com/CrunchyFashion/CrunchyFashion/chest.jpg",
      },
    ],
  };

  // Convert measurements to cm
  const convertToCm = (inchRange: string): string => {
    return inchRange
      .split("-")
      .map((val: string) => {
        const num = parseFloat(val);
        return (num * 2.54).toFixed(1);
      })
      .join("-");
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-2 xs:px-3 sm:px-4 py-4 xs:py-6 sm:py-8 text-gray-800">
      {/* Header */}
      <h1 className="text-center text-xl xs:text-2xl sm:text-3xl font-light mb-4 xs:mb-6 sm:mb-8">
        Fit Guide
      </h1>

      {/* Unit Toggle Switch */}
      <div className="flex justify-center items-center mb-4 xs:mb-6 sm:mb-8">
        <span
          className={`mr-2 xs:mr-3 text-xs xs:text-sm sm:text-base ${
            !isCm ? "font-semibold" : "text-gray-500"
          }`}
        >
          Inches
        </span>

        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={isCm}
            onChange={(e) => setIsCm(e.target.checked)}
          />
          <div className="w-10 h-5 xs:w-11 xs:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] xs:after:top-[2px] xs:after:left-[2px] after:bg-black after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 xs:after:h-5 xs:after:w-5 after:transition-all"></div>
        </label>

        <span
          className={`ml-2 xs:ml-3 text-xs xs:text-sm sm:text-base ${
            isCm ? "font-semibold" : "text-gray-500"
          }`}
        >
          Centimeters
        </span>
      </div>

      {/* Upper Wear - Mobile Cards & Desktop Table */}
      <div className="mb-6 xs:mb-8 sm:mb-12">
        <h3 className="font-medium text-base xs:text-lg sm:text-xl mb-2 xs:mb-3 sm:mb-4 text-gray-700 border-b pb-1 xs:pb-2">
          Upper Wear ({unit.toUpperCase()})
        </h3>

        {/* Mobile View - Cards */}
        <div className="block sm:hidden space-y-2 xs:space-y-3">
          {data.upperWear.map((row, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-lg p-2 xs:p-3 shadow-xs"
            >
              <div className="font-semibold text-sm xs:text-base mb-2 xs:mb-3 pb-1 xs:pb-2 border-b">
                Size {row.size}
              </div>
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-1 xs:gap-2 text-xs xs:text-sm">
                <div className="flex justify-between xs:block">
                  <span className="text-gray-600 xs:mr-1">Bust:</span>
                  <span className="font-medium">
                    {isCm ? convertToCm(row.bust) : row.bust}
                  </span>
                </div>
                <div className="flex justify-between xs:block">
                  <span className="text-gray-600 xs:mr-1">Waist:</span>
                  <span className="font-medium">
                    {isCm ? convertToCm(row.waist) : row.waist}
                  </span>
                </div>
                <div className="flex justify-between xs:block">
                  <span className="text-gray-600 xs:mr-1">Hip:</span>
                  <span className="font-medium">
                    {isCm ? convertToCm(row.hip) : row.hip}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View - Table */}
        <div className="hidden sm:block overflow-x-auto shadow-sm">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 text-left font-semibold w-16">Size</th>
                <th className="p-3 text-left font-semibold w-32">Bust</th>
                <th className="p-3 text-left font-semibold w-32">Waist</th>
                <th className="p-3 text-left font-semibold w-32">Hip</th>
              </tr>
            </thead>
            <tbody>
              {data.upperWear.map((row, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="p-3 font-medium">{row.size}</td>
                  <td className="p-3">
                    {isCm ? convertToCm(row.bust) : row.bust}
                  </td>
                  <td className="p-3">
                    {isCm ? convertToCm(row.waist) : row.waist}
                  </td>
                  <td className="p-3">
                    {isCm ? convertToCm(row.hip) : row.hip}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Wear - Mobile Cards & Desktop Table */}
      <div className="mb-6 xs:mb-8 sm:mb-12">
        <h3 className="font-medium text-base xs:text-lg sm:text-xl mb-2 xs:mb-3 sm:mb-4 text-gray-700 border-b pb-1 xs:pb-2">
          Bottom Wear ({unit.toUpperCase()})
        </h3>

        {/* Mobile View - Cards */}
        <div className="block sm:hidden space-y-2 xs:space-y-3">
          {data.bottomWear.map((row, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-lg p-2 xs:p-3 shadow-xs"
            >
              <div className="font-semibold text-sm xs:text-base mb-2 xs:mb-3 pb-1 xs:pb-2 border-b">
                Size {row.size}
              </div>
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-1 xs:gap-2 text-xs xs:text-sm">
                <div className="flex justify-between xs:block">
                  <span className="text-gray-600 xs:mr-1">Waist:</span>
                  <span className="font-medium">
                    {isCm ? convertToCm(row.waist) : row.waist}
                  </span>
                </div>
                <div className="flex justify-between xs:block">
                  <span className="text-gray-600 xs:mr-1">Hip:</span>
                  <span className="font-medium">
                    {isCm ? convertToCm(row.hip) : row.hip}
                  </span>
                </div>
                <div className="flex justify-between xs:block">
                  <span className="text-gray-600 xs:mr-1">Thigh:</span>
                  <span className="font-medium">
                    {isCm ? convertToCm(row.thigh) : row.thigh}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View - Table */}
        <div className="hidden sm:block overflow-x-auto shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 text-left font-semibold">Size</th>
                <th className="p-3 text-left font-semibold">Waist</th>
                <th className="p-3 text-left font-semibold">Hip</th>
                <th className="p-3 text-left font-semibold">Thigh</th>
              </tr>
            </thead>
            <tbody>
              {data.bottomWear.map((row, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="p-3 font-medium">{row.size}</td>
                  <td className="p-3">
                    {isCm ? convertToCm(row.waist) : row.waist}
                  </td>
                  <td className="p-3">
                    {isCm ? convertToCm(row.hip) : row.hip}
                  </td>
                  <td className="p-3">
                    {isCm ? convertToCm(row.thigh) : row.thigh}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* How to Measure Yourself */}
      <div className="w-full max-w-5xl mx-auto">
        <h2 className="text-center text-lg xs:text-xl sm:text-2xl font-light mb-4 xs:mb-6 sm:mb-8">
          How to Measure Yourself
        </h2>

        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 lg:grid-cols-3 gap-3 xs:gap-4 sm:gap-6">
          {data.howToMeasure.map((item, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="w-full aspect-[3/4]">
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-contain bg-gray-50"
                />
              </div>

              <div className="p-2 xs:p-3 sm:p-4">
                <h3 className="font-semibold text-sm xs:text-base text-gray-800">
                  {item.title}
                </h3>
                <p className="text-gray-600 mt-1 xs:mt-2 text-xs xs:text-sm leading-snug">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
